import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, ActivatedRoute } from '@angular/router';
import { ParameterService, Parameter, UpdateParameterRequest } from '../../../services/parameter.service';
import { ManualService } from '../../../services/manual.service';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { LoggerService } from '../../../core/services/logger.service';

@Component({
    selector: 'app-edit-parameter-page',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatSnackBarModule,
        MatCardModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './edit-parameter-page.component.html',
    styleUrl: './edit-parameter-page.component.scss'
})
export class EditParameterPageComponent implements OnInit {
    parameterForm: FormGroup;
    isLoading = false;
    isLoadingParameter = false;
    isUploadingFile = false;
    parameterId: string | null = null;
    parameter: Parameter | null = null;
    selectedFile: File | null = null;
    isManualParameter = false;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private fb: FormBuilder,
        // eslint-disable-next-line no-unused-vars
        private parameterService: ParameterService,
        // eslint-disable-next-line no-unused-vars
        private manualService: ManualService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private route: ActivatedRoute,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService
    ) {
        this.parameterForm = this.fb.group({
            value: ['', [Validators.maxLength(500)]],
            description: ['', [Validators.maxLength(500)]],
            isActive: [true]
        });
    }

    ngOnInit(): void {
        this.parameterId = this.route.snapshot.paramMap.get('id');
        if (this.parameterId) {
            this.loadParameter();
        } else {
            this.snackBar.open('ID de parámetro no proporcionado', 'Cerrar', {
                duration: 3000,
                panelClass: ['error-snackbar']
            });
            this.router.navigate(['/parameters']);
        }
    }

    loadParameter(): void {
        if (!this.parameterId) return;

        this.isLoadingParameter = true;
        this.parameterService.getParameterById(this.parameterId)
            .pipe(
                catchError(error => {
                    this.logger.error('Error al cargar parámetro', error as Error, 'EditParameterPageComponent');
                    const errorMessage = error.error?.message || 'Error al cargar el parámetro';
                    this.snackBar.open(errorMessage, 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar']
                    });
                    this.router.navigate(['/parameters']);
                    return of(null);
                }),
                finalize(() => {
                    this.isLoadingParameter = false;
                })
            )
            .subscribe(response => {
                if (response && response.success && response.data) {
                    this.parameter = response.data;
                    this.isManualParameter = this.parameter.key === 'MANUAL_USUARIO_PATH';
                    
                    // Si es el parámetro de manual, no requerir el campo value
                    if (this.isManualParameter) {
                        this.parameterForm.get('value')?.clearValidators();
                        this.parameterForm.get('value')?.updateValueAndValidity();
                    }
                    
                    this.parameterForm.patchValue({
                        value: this.parameter.value,
                        description: this.parameter.description || '',
                        isActive: this.parameter.isActive
                    });
                } else {
                    this.router.navigate(['/parameters']);
                }
            });
    }

    getErrorMessage(fieldName: string): string {
        const control = this.parameterForm.get(fieldName);
        if (!control) return '';

        if (control.hasError('required')) {
            return 'Este campo es requerido';
        }

        if (control.hasError('maxlength')) {
            const requiredLength = control.errors?.['maxlength']?.requiredLength;
            return `Máximo ${requiredLength} caracteres`;
        }

        return 'Campo inválido';
    }

    markFormGroupTouched(): void {
        Object.keys(this.parameterForm.controls).forEach(key => {
            const control = this.parameterForm.get(key);
            control?.markAsTouched();
        });
    }

    onSubmit(): void {
        if (this.parameterForm.valid && this.parameterId) {
            // Si es el parámetro de manual y hay un archivo seleccionado, subirlo primero
            if (this.isManualParameter && this.selectedFile) {
                this.uploadManualFile();
            } else {
                this.updateParameter();
            }
        } else {
            this.markFormGroupTouched();
        }
    }

    /**
     * Actualiza el parámetro con los valores del formulario
     */
    private updateParameter(): void {
        this.isLoading = true;
        
        const formData = this.parameterForm.value;
        const parameterData: UpdateParameterRequest = {
            value: formData.value.trim(),
            description: formData.description?.trim() || undefined,
            isActive: formData.isActive === true || formData.isActive === false ? formData.isActive : true
        };

        this.parameterService.updateParameter(this.parameterId!, parameterData)
            .pipe(
                catchError(error => {
                    this.logger.error('Error al actualizar parámetro', error as Error, 'EditParameterPageComponent');
                    const errorMessage = error.error?.message || 'Error al actualizar el parámetro';
                    this.snackBar.open(errorMessage, 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar']
                    });
                    return of(null);
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe(response => {
                if (response && response.success) {
                    this.snackBar.open('Parámetro actualizado exitosamente', 'Cerrar', {
                        duration: 3000,
                        panelClass: ['success-snackbar']
                    });
                    this.router.navigate(['/parameters']);
                }
            });
    }

    /**
     * Maneja la selección de archivo
     */
    onFileSelected(event: any): void {
        const file = event.target.files[0];
        if (file) {
            // Validar que sea PDF
            if (file.type !== 'application/pdf') {
                this.snackBar.open('El archivo debe ser un PDF', 'Cerrar', {
                    duration: 3000,
                    panelClass: ['error-snackbar']
                });
                return;
            }
            this.selectedFile = file;
        }
    }

    /**
     * Sube el archivo PDF del manual y actualiza el parámetro
     */
    private uploadManualFile(): void {
        if (!this.selectedFile) {
            this.snackBar.open('Por favor seleccione un archivo PDF', 'Cerrar', {
                duration: 3000,
                panelClass: ['error-snackbar']
            });
            return;
        }

        this.isUploadingFile = true;
        this.manualService.uploadManual(this.selectedFile)
            .pipe(
                catchError(error => {
                    this.logger.error('Error al subir manual', error as Error, 'EditParameterPageComponent');
                    const errorMessage = error instanceof Error ? error.message : 'Error al subir el manual';
                    this.snackBar.open(errorMessage, 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar']
                    });
                    return of(null);
                }),
                finalize(() => {
                    this.isUploadingFile = false;
                })
            )
            .subscribe(response => {
                if (response && response.success) {
                    // Actualizar el valor del parámetro con la ruta retornada
                    this.parameterForm.patchValue({
                        value: response.data.path
                    });
                    
                    // Actualizar el parámetro con la descripción y estado
                    this.updateParameter();
                    
                    this.snackBar.open('Manual subido exitosamente', 'Cerrar', {
                        duration: 3000,
                        panelClass: ['success-snackbar']
                    });
                }
            });
    }

    onCancel(): void {
        this.router.navigate(['/parameters']);
    }
}

